/**
 * @file Complaints.js
 * @version 1.4.3
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-22
 * @modified 2026-09-29
 * @description Search and manage customer complaints (quality / delivery issues).
 *              Rows can be checked for bulk archive / restore; every column sorts.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './Complaints.css';
import './ComplaintImages.css';
import './TableStyles.css';
import Dropdown from './Dropdown';
import Input from './Input';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import ComplaintModal from './ComplaintModal';
import ContactModal from './ContactModal';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import { formatDate } from '../utils/dateFormatters';
import usePersistedState from '../hooks/usePersistedState';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import useTableContextMenu from '../hooks/useTableContextMenu';

/* A complaint can only be archived once it is off the books, and only restored
   if it is already archived — the two are mutually exclusive per row. */
const canArchive = (c) => !c.is_archived && c.status !== 'Open';
const canRestore = (c) => !!c.is_archived;

const supplierOf = (c) =>
  c.supplier_display || c.supplier_account_name || c.supplier_name || c.haulier_name || '';

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js).
 */
const CLEARED_FILTERS = { search: '', status: '', showArchived: false, limit: '50' };

function Complaints({ user, onNavigate }) {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [selectedContactId, setSelectedContactId] = usePersistedState('complaints.contactModalId', null);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(null);

  const [filters, setFilters] = usePersistedState('complaints.filters', CLEARED_FILTERS);

  const [sorting, setSorting] = usePersistedState('complaints.sorting', {
    field: 'complaint_date',
    direction: 'desc'
  });

  const [pagination, setPagination] = usePersistedState('complaints.pagination', {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  useSlideOutButton([selectedIds.length, bulkBusy, loading]);

  useEffect(() => { loadComplaints(); }, []);

  useEffect(() => {
    const t = setTimeout(() => loadComplaints(), 300);
    return () => clearTimeout(t);
  }, [filters.status, filters.showArchived, filters.limit]);

  const loadComplaints = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 10000,
        sortField: sorting.field,
        sortDirection: sorting.direction
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.showArchived) params.append('showArchived', 'true');

      const response = await fetch(`${API_URL}/api/complaints/search?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setComplaints(data.complaints || []);
        const limit = parseInt(filters.limit) || 50;
        setPagination({
          page: 1,
          limit,
          total: (data.complaints || []).length,
          totalPages: Math.ceil((data.complaints || []).length / limit)
        });
      }
    } catch (error) {
      console.error('Error loading complaints:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to load complaints', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortArrow = (field) =>
    sorting.field === field ? (sorting.direction === 'asc' ? '▲' : '▼') : '';

  const filteredAndSorted = useMemo(() => {
    let filtered = [...complaints];
    if (filters.search) {
      filtered = filtered.filter(c => matchesAllTerms([
        c.complaint_number, c.customer_name, c.account_no,
        c.reference, c.rep_name,
        c.supplier_display, c.supplier_account_name, c.supplier_name, c.haulier_name,
        c.supplier_reference,
        c.complaint_text, c.action_taken, c.credit_note_number, c.notes
      ], filters.search));
    }

    // Sort on the value the column actually renders, not the raw field — the
    // supplier cell falls back through three columns, so sorting on
    // supplier_name alone would put most rows in an arbitrary order.
    const sortValue = (c) => {
      if (['complaint_date', 'delivery_date'].includes(sorting.field)) {
        return c[sorting.field] ? new Date(c[sorting.field]).getTime() : 0;
      }
      if (sorting.field === 'status') {
        const order = { Open: 0, Settled: 1, Closed: 2 };
        return c.is_archived ? 3 : (order[c.status] ?? 4);
      }
      if (sorting.field === 'supplier_name') return supplierOf(c).toLowerCase();
      const raw = c[sorting.field];
      return (raw === null || raw === undefined ? '' : raw.toString()).toLowerCase();
    };

    filtered.sort((a, b) => {
      const aVal = sortValue(a);
      const bVal = sortValue(b);

      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      const aDate = a.complaint_date ? new Date(a.complaint_date).getTime() : 0;
      const bDate = b.complaint_date ? new Date(b.complaint_date).getTime() : 0;
      return bDate - aDate;
    });

    return filtered;
  }, [complaints, filters.search, sorting]);

  useEffect(() => {
    const limit = parseInt(filters.limit) || 50;
    setPagination(prev => ({
      ...prev,
      page: 1,
      limit,
      total: filteredAndSorted.length,
      totalPages: Math.ceil(filteredAndSorted.length / limit)
    }));
  }, [filteredAndSorted.length, filters.search, filters.limit]);

  const paginated = filteredAndSorted.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const ctxMenu = useTableContextMenu({
    rows: paginated,
    selectedIds: selectedIdSet,
    getRowId: (c) => c.id
  });

  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const allSelected = paginated.length > 0 && paginated.every(c => selectedIds.includes(c.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : paginated.map(c => c.id));

  // Selection is only meaningful for rows still on screen — a filter change that
  // drops a checked row must not leave it silently in a bulk action.
  const selectedComplaints = useMemo(
    () => filteredAndSorted.filter(c => selectedIdSet.has(c.id)),
    [filteredAndSorted, selectedIdSet]
  );
  const archivable = selectedComplaints.filter(canArchive);
  const restorable = selectedComplaints.filter(canRestore);

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '50' }));
    setSorting({ field: 'complaint_date', direction: 'desc' });
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedIds([]);
  };

  // Any header filter lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS);
  const filtersApplied = headerFilters.count > 0;

  const handleAddNew = () => {
    setSelectedComplaint(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleView = (c) => {
    setSelectedComplaint(c);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (c) => {
    setSelectedComplaint(c);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleArchive = (c) => {
    setConfirmDialog({
      isOpen: true,
      message: `Archive complaint ${c.complaint_number} for ${c.customer_name}?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await fetch(`${API_URL}/api/complaints/${c.id}/archive`, {
            method: 'PUT', credentials: 'include'
          });
          if (response.ok) {
            setAlertDialog({ isOpen: true, message: 'Complaint archived', type: 'success' });
            loadComplaints();
          } else {
            setAlertDialog({ isOpen: true, message: 'Failed to archive complaint', type: 'error' });
          }
        } catch (error) {
          console.error('Archive error:', error);
          setAlertDialog({ isOpen: true, message: 'Error archiving complaint', type: 'error' });
        }
      }
    });
  };

  const handleRestore = (c) => {
    setConfirmDialog({
      isOpen: true,
      message: `Restore complaint ${c.complaint_number} for ${c.customer_name}?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await fetch(`${API_URL}/api/complaints/${c.id}/restore`, {
            method: 'PUT', credentials: 'include'
          });
          if (response.ok) {
            setAlertDialog({ isOpen: true, message: 'Complaint restored', type: 'success' });
            loadComplaints();
          } else {
            setAlertDialog({ isOpen: true, message: 'Failed to restore complaint', type: 'error' });
          }
        } catch (error) {
          console.error('Restore error:', error);
          setAlertDialog({ isOpen: true, message: 'Error restoring complaint', type: 'error' });
        }
      }
    });
  };

  /* Bulk archive / restore run the same per-complaint endpoints in sequence —
     there is no bulk route, and at this volume there does not need to be. Each
     one is reported individually so a partial failure is visible rather than
     swallowed by an overall "done". */
  const runBulk = (mode) => {
    const targets = mode === 'archive' ? archivable : restorable;
    if (!targets.length) return;
    const verb = mode === 'archive' ? 'Archive' : 'Restore';
    setConfirmDialog({
      isOpen: true,
      message: `${verb} ${targets.length} complaint${targets.length !== 1 ? 's' : ''}?`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        setBulkBusy(mode);
        let done = 0;
        const failed = [];
        for (const c of targets) {
          try {
            const response = await fetch(`${API_URL}/api/complaints/${c.id}/${mode}`, {
              method: 'PUT', credentials: 'include'
            });
            if (response.ok) done++; else failed.push(c.complaint_number);
          } catch (error) {
            console.error(`Bulk ${mode} error:`, error);
            failed.push(c.complaint_number);
          }
        }
        setBulkBusy(null);
        setSelectedIds([]);
        setAlertDialog({
          isOpen: true,
          type: failed.length ? 'error' : 'success',
          message: failed.length
            ? `${verb}d ${done}, failed on ${failed.join(', ')}`
            : `${verb}d ${done} complaint${done !== 1 ? 's' : ''}`
        });
        loadComplaints();
      }
    });
  };

  const handleSave = () => {
    setShowModal(false);
    loadComplaints();
  };

  const buildRowActions = (c) => [
    { key: 'view', readSafe: true, icon: 'visibility', label: 'View', title: 'View', onClick: () => handleView(c) },
    { key: 'edit', icon: 'edit', label: 'Edit', title: 'Edit', onClick: () => handleEdit(c) },
    c.is_archived
      ? {
          key: 'archive',
          icon: 'unarchive',
          label: 'Restore',
          title: 'Restore from archive',
          onClick: () => handleRestore(c)
        }
      : {
          key: 'archive',
          icon: 'archive',
          label: 'Archive',
          className: 'btn-danger',
          disabled: c.status === 'Open',
          title: c.status === 'Open' ? 'Only closed/settled complaints can be archived' : 'Archive',
          onClick: () => { if (c.status !== 'Open') handleArchive(c); }
        },
  ];

  const bulkActions = () => [
    {
      key: 'bulk-archive',
      icon: 'archive',
      label: `Archive ${archivable.length}`,
      disabled: archivable.length === 0,
      title: archivable.length === 0 ? 'Nothing selected that can be archived' : 'Archive selected',
      onClick: () => runBulk('archive')
    },
    {
      key: 'bulk-restore',
      icon: 'unarchive',
      label: `Restore ${restorable.length}`,
      disabled: restorable.length === 0,
      title: restorable.length === 0 ? 'Nothing selected that is archived' : 'Restore selected',
      onClick: () => runBulk('restore')
    },
    {
      key: 'bulk-clear',
      icon: 'close',
      label: 'Clear selection',
      onClick: () => setSelectedIds([])
    }
  ];

  const getStatusDisplay = (c) => {
    if (c.is_archived) return { label: 'Archived', className: 'archived' };
    if (c.status === 'Open') return { label: 'Open', className: 'active' };
    if (c.status === 'Settled') return { label: 'Settled', className: 'settled' };
    return { label: 'Closed', className: 'draft' };
  };

  const openCount = complaints.filter(c => c.status === 'Open' && !c.is_archived).length;
  const settledCount = complaints.filter(c => c.status === 'Settled' && !c.is_archived).length;
  const closedCount = complaints.filter(c => c.status === 'Closed' && !c.is_archived).length;

  return (
    <div className="complaints">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Track and manage customer complaints</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Open: {openCount} | Settled: {settledCount} | Closed: {closedCount}
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add Complaint</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['complaint no.', 'customer', 'account no.', 'reference', 'rep', 'supplier', 'haulier', 'supplier ref.', 'complaint', 'action taken', 'credit note no.', 'notes']} />
            </label>
            <Input
              type="text"
              placeholder="Customer, supplier, complaint, ref..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Status</label>
            <Dropdown
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={[
                { value: '', label: 'All' },
                { value: 'Open', label: 'Open' },
                { value: 'Settled', label: 'Settled' },
                { value: 'Closed', label: 'Closed' }
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Show Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="complaints-show-archived"
                  type="checkbox"
                  checked={filters.showArchived}
                  onChange={(e) => handleFilterChange('showArchived', e.target.checked)}
                />
                <label htmlFor="complaints-show-archived"></label>
              </div>
            </div>
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="50"
            />
          </div>
        </div>
      </div>

      <div className="complaints-panel">
        {selectedComplaints.length > 0 && (
          <div className="bulk-actions-bar">
            <div className="bulk-actions-info">
              <span className="bulk-actions-title">Bulk Actions:</span>
              <span>{selectedComplaints.length} complaint{selectedComplaints.length !== 1 ? 's' : ''} selected</span>
            </div>
            <div className="bulk-actions-buttons slideout">
              <div>
                <button
                  className="btn-slideout"
                  onClick={() => runBulk('archive')}
                  disabled={archivable.length === 0 || !!bulkBusy}
                  title={archivable.length === 0
                    ? 'Only closed or settled complaints can be archived'
                    : `Archive ${archivable.length} of ${selectedComplaints.length} selected`}
                >
                  <div className="icon-wrapper">
                    <span className={`material-symbols-rounded${bulkBusy === 'archive' ? ' spinning' : ''}`}>
                      {bulkBusy === 'archive' ? 'progress_activity' : 'archive'}
                    </span>
                  </div>
                  <span className="btn-label">
                    {bulkBusy === 'archive' ? 'Archiving…' : `Archive ${archivable.length}`}
                  </span>
                </button>
              </div>
              <div>
                <button
                  className="btn-slideout"
                  onClick={() => runBulk('restore')}
                  disabled={restorable.length === 0 || !!bulkBusy}
                  title={restorable.length === 0
                    ? 'None of the selected complaints are archived'
                    : `Restore ${restorable.length} of ${selectedComplaints.length} selected`}
                >
                  <div className="icon-wrapper">
                    <span className={`material-symbols-rounded${bulkBusy === 'restore' ? ' spinning' : ''}`}>
                      {bulkBusy === 'restore' ? 'progress_activity' : 'unarchive'}
                    </span>
                  </div>
                  <span className="btn-label">
                    {bulkBusy === 'restore' ? 'Restoring…' : `Restore ${restorable.length}`}
                  </span>
                </button>
              </div>
              <div>
                <button data-read-safe className="btn-round btn-danger" onClick={() => setSelectedIds([])} disabled={!!bulkBusy}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">close</span>
                  </div>
                  <span className="btn-label">Clear</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="table-toolbar">
          {pagination.totalPages > 0 && (
            <PaginationControls pagination={pagination} onPageChange={changePage} />
          )}
        </div>

        <div className="table-container">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <table className="data-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th className="checkbox-col no-sort">
                    <TickCheckbox
                      id="complaints-select-all"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      ariaLabel="Select all complaints on this page"
                      disabled={paginated.length === 0}
                    />
                  </th>
                  <th className="sortable" onClick={() => handleSort('complaint_number')}>
                    Ref {sortArrow('complaint_number')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('complaint_date')}>
                    Date {sortArrow('complaint_date')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('customer_name')}>
                    Customer {sortArrow('customer_name')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('rep_name')}>
                    Rep {sortArrow('rep_name')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('complaint_text')}>
                    Complaint {sortArrow('complaint_text')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('supplier_name')}>
                    Supplier / Haulier {sortArrow('supplier_name')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('action_taken')}>
                    Action {sortArrow('action_taken')}
                  </th>
                  <th className="status-col sortable" onClick={() => handleSort('status')}>
                    Status {sortArrow('status')}
                  </th>
                  <th className="actions-col no-sort"></th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center">No complaints found</td>
                  </tr>
                ) : (
                  paginated.map((c) => {
                    const statusDisplay = getStatusDisplay(c);
                    const isSelected = selectedIdSet.has(c.id);
                    return (
                      <tr
                        key={c.id}
                        className={`${c.is_archived ? 'inactive-row' : ''}${isSelected ? ' selected' : ''}`}
                        onContextMenu={ctxMenu.onRowContextMenu(c)}
                      >
                        <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                          <TickCheckbox
                            id={`complaint-${c.id}`}
                            checked={isSelected}
                            onChange={() => toggleSelect(c.id)}
                            ariaLabel={`Select ${c.complaint_number}`}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span className='ref-badge'>{c.complaint_number}</span>
                          {/* This complaint has photographs. Beside the
                              reference rather than in a column of its own: most
                              rows would have an empty cell, and a column that is
                              usually blank costs more width than it returns. */}
                            <span className="complaint-media">
                            {c.image_count > 0 && (
                              <span
                                className="material-symbols-rounded complaint-has-images"
                                title={`${c.image_count} photograph${c.image_count === 1 ? '' : 's'} attached`}
                              >
                                photo_camera
                              </span>
                            )}
                            {c.video_count > 0 && (
                              <span
                                className="material-symbols-rounded complaint-has-videos"
                                title={`${c.video_count} video${c.video_count === 1 ? '' : 's'} attached`}
                              >
                                videocam
                              </span>
                            )}
                          </span>
                        </td>
                        <td>{formatDate(c.complaint_date)}</td>
                        <td>
                          <span className="name-with-badge">
                            <span
                              className={`account-badge-inline${!c.account_no ? ' empty' : ''}`}
                              style={(c.account_id && c.account_no) ? { cursor: 'pointer' } : undefined}
                              onClick={(c.account_id && c.account_no) ? (e) => { e.stopPropagation(); setSelectedContactId(c.account_id); } : undefined}
                            >
                              {c.account_no || '\u00A0'}
                            </span>
                            <span
                              className="contact-link"
                              onClick={(e) => { e.stopPropagation(); if (c.account_id) setSelectedContactId(c.account_id); }}
                            >
                              {c.customer_name}
                            </span>
                          </span>
                        </td>
                        <td>{c.rep_name || '-'}</td>
                        <td className="complaint-cell" title={c.complaint_text}>{c.complaint_text}</td>
                        <td>
                          <div>{c.supplier_display || c.supplier_account_name || c.supplier_name || '-'}</div>
                          {c.haulier_name && (
                            <div className="haulier-line">{c.haulier_name}</div>
                          )}
                        </td>
                        <td className="action-cell" title={c.action_taken}>{c.action_taken || '-'}</td>
                        <td className="status-col">
                          <span className={`status-badge ${statusDisplay.className}`}>
                            {statusDisplay.label}
                          </span>
                        </td>
                        <td className="actions-col">
                          <RowActionsMenu actions={buildRowActions(c)} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <ComplaintModal
          complaint={selectedComplaint}
          mode={modalMode}
          onClose={() => { setShowModal(false); loadComplaints(); }}
          onSave={handleSave}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null })}
      />

      {selectedContactId && (
        <ContactModal
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
        />
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={bulkActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add complaint', onClick: handleAddNew },
          { key: 'refresh', readSafe: true, icon: 'refresh', label: 'Refresh', onClick: loadComplaints },
          {
            key: 'select-all',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: allSelected ? 'deselect' : 'select_all',
            label: allSelected ? 'Deselect all' : 'Select all on page',
            disabled: paginated.length === 0,
            onClick: toggleSelectAll
          },
          {
            key: 'show-archived',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'inventory_2',
            label: 'Show archived',
            active: filters.showArchived,
            onClick: () => handleFilterChange('showArchived', !filters.showArchived)
          },
          { key: 'clear-filters', readSafe: true, icon: 'filter_alt_off', label: 'Clear filters', onClick: handleClearFilters }
        ]}
      />
    </div>
  );
}

export default Complaints;
